import { useEffect, useState } from 'react'
import { assignable, listValues, type Axis, type HypothesisValue } from '../hypotheses/dictionaryApi'

export type DictionaryPhase = 'idle' | 'loading' | 'ready' | 'failed'

export interface Dictionary {
  /** `idle` until the options are wanted; a phase always answers the current key. */
  phase: DictionaryPhase
  /** The assignable values of the current key: neither archived nor seeded. */
  values: HypothesisValue[]
  /** A read under the current key answered, so the picker can offer its values. */
  fresh: boolean
  retry: () => void
}

type Loaded =
  | { phase: 'loading' }
  | { phase: 'ready'; values: HypothesisValue[] }
  | { phase: 'failed' }

/**
 * The assign options of one axis for one company (GRO-170), read when `wanted` first
 * holds and again when the shell raises `revision` after a dictionary write.
 *
 * Everything is keyed on `(companyId, axis, revision)`. Options loaded under another key
 * neither render nor count as fresh, and a response that lands after its key changed is
 * dropped, so a screen that is not remounted on a company switch never offers one
 * company's values in another.
 */
export function useDictionary(
  companyId: string,
  axis: Axis,
  revision: number,
  wanted: boolean,
  fetchImpl?: typeof fetch,
): Dictionary {
  const key = `${companyId}|${axis}|${revision}`
  const [loaded, setLoaded] = useState<{ key: string; state: Loaded } | null>(null)
  const [attempt, setAttempt] = useState(0)
  const current = loaded !== null && loaded.key === key ? loaded.state : null
  // Only a read that answered counts: one discarded while loading, because the options
  // stopped being wanted under it, is read again when they are wanted next.
  const fresh = current?.phase === 'ready'

  useEffect(() => {
    if (!wanted || fresh) return
    let live = true
    setLoaded({ key, state: { phase: 'loading' } })
    void listValues(companyId, axis, fetchImpl).then((outcome) => {
      if (!live || outcome.status === 'stale' || outcome.status === 'expired') return
      setLoaded({
        key,
        state: outcome.status === 'ok' ? { phase: 'ready', values: assignable(outcome.body) } : { phase: 'failed' },
      })
    })
    return () => { live = false }
    // `fresh` and not `loaded`: the effect sets `loaded` itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted, key, attempt, fetchImpl])

  return {
    phase: current?.phase ?? 'idle',
    values: current?.phase === 'ready' ? current.values : [],
    fresh,
    retry: () => setAttempt((n) => n + 1),
  }
}
